import { PropsWithChildren } from 'react';
import { Box, Button, Link, Stack, styled, Tab, Tabs, Typography } from '@mui/material';

import { AccessSessionDetailsClientModel } from '@apono/client-app-sdk';

import OpenReplayObscured from '@common/utils/analytics/OpenReplayObscured';
import { MaterialIcon } from '@common/ui/MaterialIcon';
import Markdown from '@common/ui/Markdown';

import { AccessDetailsTabs } from '@MyAccess/utils/types';
import { AnalyticsEvents } from '@common/utils/analytics/events';

const LinkButton = styled(Button)<{
  component: typeof Link;
  target: string;
}>({
  position: 'absolute',
  right: 0,
  top: '50%',
  transform: 'translateY(-50%)',
});

interface AccessDetailsTabbedProps {
  accessDetails: AccessSessionDetailsClientModel;
  activeTab: string;
  onTabChange: (tab: string) => void;
  isAlreadyViewed?: boolean;
}

export default function AccessDetailsTabbed({
  accessDetails,
  activeTab,
  onTabChange,
  isAlreadyViewed,
}: AccessDetailsTabbedProps) {
  return (
    <Stack spacing={2}>
      <Tabs value={activeTab} onChange={(_, v) => onTabChange(v)}>
        {accessDetails.json && <Tab label="Parameters" value={AccessDetailsTabs.Parameters} />}
        {accessDetails.cli && <Tab label="CLI" value={AccessDetailsTabs.CLI} />}
        <Tab label="Instructions" value={AccessDetailsTabs.Instructions} />
        {accessDetails.link && (
          <LinkButton
            href={accessDetails.link.url}
            target="_blank"
            component={Link}
            variant="text"
            size="small"
            startIcon={<MaterialIcon symbol="open_in_new" weight={800} />}
            data-trigger={AnalyticsEvents.LinkToConsoleInitiated}
          >
            {accessDetails.link.title || 'Open In Browser'}
          </LinkButton>
        )}
      </Tabs>
      {accessDetails.json && (
        <CredentialsTab
          isActive={activeTab == AccessDetailsTabs.Parameters}
          credentials={accessDetails.json}
          copyDisabled={isAlreadyViewed}
        />
      )}
      {accessDetails.cli && (
        <CliTab isActive={activeTab == AccessDetailsTabs.CLI} cli={accessDetails.cli} copyDisabled={isAlreadyViewed} />
      )}
      <InstructionsTab
        isActive={activeTab == AccessDetailsTabs.Instructions}
        instructions={accessDetails.instructions.markdown || accessDetails.instructions.plain}
        copyDisabled={isAlreadyViewed}
      />
    </Stack>
  );
}

interface AccessDetailsTabProps {
  isActive: boolean;
}

function AccessDetailsTab({ isActive = false, children }: PropsWithChildren<AccessDetailsTabProps>) {
  return (
    <div role="tabpanel" hidden={!isActive}>
      {isActive && (
        <OpenReplayObscured>
          <Box sx={{ pX: 2 }}>{children}</Box>
        </OpenReplayObscured>
      )}
    </div>
  );
}

function InstructionsTab({
  isActive,
  instructions,
  copyDisabled,
}: {
  isActive: boolean;
  instructions: string;
  copyDisabled?: boolean;
}) {
  return (
    <AccessDetailsTab isActive={isActive}>
      <Typography variant="body2" component="div">
        <Markdown copyDisabled={copyDisabled}>{instructions}</Markdown>
      </Typography>
    </AccessDetailsTab>
  );
}

function CliTab({ isActive, cli, copyDisabled }: { isActive: boolean; cli: string; copyDisabled?: boolean }) {
  const command = `
\`\`\`
${cli}
\`\`\`
`;

  return (
    <AccessDetailsTab isActive={isActive}>
      <CodeBlock
        label="Run the following command to connect using the CLI:"
        code={command}
        copyDisabled={copyDisabled}
      />
    </AccessDetailsTab>
  );
}

function CredentialsTab({
  isActive,
  credentials,
  copyDisabled,
}: {
  isActive: boolean;
  credentials: object;
  copyDisabled?: boolean;
}) {
  const creds = Object.entries(credentials).map(([key, value]) => ({
    key,
    value: `
\`\`\`
${value}
\`\`\`
    `,
  }));

  return (
    <AccessDetailsTab isActive={isActive}>
      <Stack direction="column" justifyContent="center" alignItems="stretch" spacing={1}>
        {creds.map(({ key, value }) => (
          <CodeBlock key={key} label={`${key}:`} code={value} copyDisabled={copyDisabled} />
        ))}
      </Stack>
    </AccessDetailsTab>
  );
}

function CodeBlock({ label, code, copyDisabled }: { label: string; code: string; copyDisabled?: boolean }) {
  return (
    <Box>
      <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
        {label}
      </Typography>
      <Typography variant="body2" component="div">
        <Markdown copyDisabled={copyDisabled}>{code}</Markdown>
      </Typography>
    </Box>
  );
}
